import { Skeleton } from "@chakra-ui/react";
import React from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { useWeglot, useWeglotLanguage } from "src/plugins/weglot";
import { WEGLOT_APPLY_CLASS } from "src/plugins/weglot/constants";
import { isNonEmptyString, isNullOrUndefined } from "src/services/predicates";
import * as RD from "src/types/remoteData";

const cache = new Map<string, Map<string, string[]>>();

type Item = {
  key: string;
  label?: string;
  translate?: boolean;
};

type Props<T extends Item> = {
  items: T[];
  children: (translatedItems: T[]) => React.ReactNode;
};
export function WithTranslation<T extends Item>({
  items,
  children,
}: Props<T>): React.ReactNode {
  const weglot = useWeglot();
  const currentLanguage = useWeglotLanguage();
  const [itemsRD, setItemsRD] = React.useState<RD.RemoteData<Error, T[]>>(
    RD.loading()
  );

  React.useEffect(() => {
    if (!isNonEmptyString(currentLanguage) || isNullOrUndefined(weglot)) {
      setItemsRD(RD.success(items));
      return;
    }

    if (currentLanguage === "en") {
      setItemsRD(RD.success(items));
      return;
    }

    const doTask = async () => {
      setItemsRD(RD.loading());
      try {
        const key = JSON.stringify(items);
        let translationCache = cache.get(key);
        if (translationCache === undefined) {
          translationCache = new Map<string, string[]>();
          cache.set(key, translationCache);
        }

        let translatedLabels = translationCache.get(currentLanguage);
        if (translatedLabels === undefined) {
          translatedLabels = await weglot.translate({
            words: items.map((item) => ({
              t: 1,
              w:
                item.translate === true && isNonEmptyString(item.label)
                  ? item.label
                  : "",
            })),
            languageTo: currentLanguage,
          });
          translationCache.set(currentLanguage, translatedLabels);
        }

        const labels = translatedLabels;
        const translatedItems = items.map((item, index) => {
          const label = labels[index];
          return {
            ...item,
            label: isNonEmptyString(label) ? label : item.label,
          };
        });

        setItemsRD(RD.success(translatedItems));
      } catch (error) {
        console.error(error);
        // Fallback to english options instead of error out to prevent parent from being stuck with this question.
        setItemsRD(RD.success(items));
      }
    };

    doTask();
  }, [currentLanguage, items, weglot]);
  return (
    <RemoteDataView
      remoteData={itemsRD}
      loading={<Skeleton height="2rem" width="100%" />}
      error={() => <GenericError variant="inline" />}
      reloading={null}
    >
      {(items) => <span className={WEGLOT_APPLY_CLASS}>{children(items)}</span>}
    </RemoteDataView>
  );
}
